import { useState } from "react";
import { MapPin, Clock, Monitor, Users, ArrowRight, Mail, Heart } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Live = () => {
  const [formData, setFormData] = useState({ name: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [donationAmount, setDonationAmount] = useState(10);
  const [showDonateForm, setShowDonateForm] = useState(false);
  const [isDonating, setIsDonating] = useState(false);

  const handleThursdaySignup = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await fetch(`${API_URL}/api/sessions/thursday-free`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      setSubmitted(true);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDonate = async () => {
    if (donationAmount < 1) return;
    setIsDonating(true);
    
    try {
      const response = await fetch(`${API_URL}/api/sessions/donate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: donationAmount,
          origin_url: window.location.origin
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Error:", error);
      setIsDonating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5]" data-testid="live-page">
      
      {/* Minimal header for this page */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#FAF8F5]/90 backdrop-blur-sm border-b border-[#E8E4DE]">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="text-[#3D3D3D] font-serif text-xl" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Somalume
          </a>
          <a 
            href="https://somalume.org" 
            className="text-[#8B8B8B] text-sm hover:text-[#C9A66B] transition-colors"
          >
            Main Site →
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="min-h-[80vh] flex items-center justify-center px-6 py-20 pt-32">
        <div className="max-w-2xl mx-auto text-center">
          <h1 
            className="font-serif text-4xl md:text-5xl lg:text-6xl text-[#3D3D3D] mb-6 leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Feel steady in your body again
          </h1>
          <p className="text-[#6B6B6B] text-lg md:text-xl leading-relaxed mb-10 max-w-xl mx-auto">
            Simple guided sessions to help you regulate your nervous system, recognise your patterns, and respond instead of react.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="#sessions" 
              className="inline-flex items-center justify-center gap-2 bg-[#C9A66B] text-white px-8 py-4 rounded-full hover:bg-[#B8956A] transition-colors text-sm tracking-wide"
            >
              Join a Session
            </a>
            <a 
              href="https://somalume.org/luma"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 border border-[#C9A66B] text-[#C9A66B] px-8 py-4 rounded-full hover:bg-[#C9A66B]/10 transition-colors text-sm tracking-wide"
            >
              Try Luma
            </a>
          </div>
        </div>
      </section>

      {/* Tuesday Session */}
      <section id="sessions" className="py-20 px-6 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#C9A66B] text-sm tracking-widest uppercase mb-4 block">In-Person</span>
            <h2 
              className="font-serif text-3xl md:text-4xl text-[#3D3D3D] mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Tuesday Breathwork
            </h2>
            <p className="text-[#8B8B8B] text-sm">Inner-Light Meditation Centre</p>
          </div>
          
          <div className="bg-[#FAF8F5] rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="flex items-start gap-3">
                <MapPin size={20} className="text-[#C9A66B] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[#6B6B6B] text-sm">Location</p>
                  <p className="text-[#3D3D3D]">Inner-Light Meditation Centre</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock size={20} className="text-[#C9A66B] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[#6B6B6B] text-sm">Time</p>
                  <p className="text-[#3D3D3D]">6:00pm – 7:00pm</p>
                  <p className="text-[#8B8B8B] text-xs">Doors open 5:30pm</p>
                </div>
              </div>
            </div>
            
            <p className="text-[#6B6B6B] leading-relaxed mb-8">
              A guided breathwork session to help you release tension, settle your system, and reconnect with your body in a calm, supportive environment.
            </p>
            
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <span className="text-[#3D3D3D] text-2xl font-light">$25</span>
                <span className="text-[#8B8B8B] text-sm ml-2">per session</span>
              </div>
              <button 
                className="inline-flex items-center gap-2 bg-[#C9A66B] text-white px-6 py-3 rounded-full hover:bg-[#B8956A] transition-colors text-sm cursor-default"
              >
                Reserve Your Spot
              </button>
            </div>
            <p className="text-[#8B8B8B] text-sm mt-4 text-center">
              To reserve your place, message Ben via the contact details below.
            </p>
          </div>
        </div>
      </section>

      {/* Thursday Session (Free) */}
      <section className="py-20 px-6 bg-[#FAF8F5]">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#C9A66B] text-sm tracking-widest uppercase mb-4 block">Online · Free</span>
            <h2 
              className="font-serif text-3xl md:text-4xl text-[#3D3D3D] mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Free Nervous System Reset
            </h2>
            <p className="text-[#8B8B8B] text-sm">Every Thursday</p>
          </div>
          
          <div className="bg-white rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="flex items-start gap-3">
                <Monitor size={20} className="text-[#C9A66B] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[#6B6B6B] text-sm">Format</p>
                  <p className="text-[#3D3D3D]">Online (Zoom)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Clock size={20} className="text-[#C9A66B] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[#6B6B6B] text-sm">Time</p>
                  <p className="text-[#3D3D3D]">6:00pm – 6:45pm</p>
                </div>
              </div>
            </div>
            
            <p className="text-[#6B6B6B] leading-relaxed mb-8">
              A simple, guided session to help you slow down, breathe, and reset your state.
              <br /><br />
              <span className="text-[#8B8B8B] italic">No experience needed.</span>
            </p>
            
            {submitted ? (
              <div className="text-center py-8 bg-[#F0EDE8] rounded-xl">
                <p className="text-[#3D3D3D] text-lg mb-3">You're in!</p>
                <p className="text-[#6B6B6B] text-sm mb-4">Check your email for the Zoom details.</p>
                <p className="text-[#8B8B8B] text-xs">Don't see it? Check your spam or junk folder.<br/>Emails can sometimes take a few minutes to arrive.</p>
              </div>
            ) : (
              <form onSubmit={handleThursdaySignup} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Your name"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full bg-[#FAF8F5] border-0 rounded-xl px-5 py-4 text-[#3D3D3D] placeholder:text-[#AAAAAA] focus:outline-none focus:ring-2 focus:ring-[#C9A66B]/30"
                  />
                  <input
                    type="email"
                    placeholder="Email address"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="w-full bg-[#FAF8F5] border-0 rounded-xl px-5 py-4 text-[#3D3D3D] placeholder:text-[#AAAAAA] focus:outline-none focus:ring-2 focus:ring-[#C9A66B]/30"
                  />
                </div>
                <button 
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#C9A66B] text-white py-4 rounded-full hover:bg-[#B8956A] transition-colors text-sm disabled:opacity-50"
                >
                  {isSubmitting ? "Joining..." : "Join This Session"}
                </button>
              </form>
            )}
            
            {/* Donation Option */}
            <div className="mt-8 pt-6 border-t border-[#E8E4DE] text-center">
              <p className="text-[#8B8B8B] text-sm mb-4">This session is free. If you'd like to support this work:</p>
              
              {!showDonateForm ? (
                <button 
                  onClick={() => setShowDonateForm(true)}
                  className="inline-flex items-center gap-2 text-[#C9A66B] text-sm hover:underline"
                >
                  <Heart size={16} />
                  Make a donation
                </button>
              ) : (
                <div className="max-w-xs mx-auto space-y-4">
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-[#6B6B6B]">$</span>
                    <input
                      type="number"
                      min="1"
                      value={donationAmount}
                      onChange={(e) => setDonationAmount(Math.max(1, parseInt(e.target.value) || 0))}
                      className="w-20 bg-[#FAF8F5] border border-[#E8E4DE] rounded-lg px-3 py-2 text-center text-[#3D3D3D] focus:outline-none focus:border-[#C9A66B]"
                    />
                    <span className="text-[#8B8B8B] text-sm">AUD</span>
                  </div>
                  <div className="flex gap-2 justify-center">
                    {[5, 10, 20, 50].map((amt) => (
                      <button
                        key={amt}
                        onClick={() => setDonationAmount(amt)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          donationAmount === amt 
                            ? 'bg-[#C9A66B] text-white' 
                            : 'bg-[#FAF8F5] text-[#6B6B6B] hover:bg-[#E8E4DE]'
                        }`}
                      >
                        ${amt}
                      </button>
                    ))}
                  </div>
                  <button
                    onClick={handleDonate}
                    disabled={isDonating}
                    className="w-full bg-[#C9A66B] text-white py-3 rounded-full hover:bg-[#B8956A] transition-colors text-sm disabled:opacity-50"
                  >
                    {isDonating ? "Redirecting..." : `Donate $${donationAmount}`}
                  </button>
                  <button
                    onClick={() => setShowDonateForm(false)}
                    className="text-[#8B8B8B] text-xs hover:text-[#6B6B6B]"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Luma Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-2xl mx-auto text-center">
          <h2 
            className="font-serif text-3xl md:text-4xl text-[#3D3D3D] mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Meet Luma
          </h2>
          
          <p className="text-[#6B6B6B] leading-relaxed mb-4">
            Luma is a simple Somalume check-in designed to help you notice your state, settle your system, and take one grounded next step.
          </p>
          <p className="text-[#8B8B8B] italic mb-10">
            You don't need to fix anything.<br />
            Just become aware enough to move well.
          </p>
          
          <a 
            href="https://somalume.org/luma"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#C9A66B] text-white px-8 py-4 rounded-full text-sm tracking-wide hover:bg-[#B8956A] transition-colors"
          >
            Try Luma
          </a>
        </div>
      </section>

      {/* 4-Week Program */}
      <section className="py-20 px-6 bg-[#F5F0E8]">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <span className="text-[#C9A66B] text-sm tracking-widest uppercase mb-4 block">4-Week Program</span>
            <h2 
              className="font-serif text-3xl md:text-4xl text-[#3D3D3D] mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Nervous System Foundations
            </h2>
          </div>
          
          <div className="bg-white rounded-2xl p-8 md:p-12">
            <div className="grid grid-cols-2 gap-6 mb-8">
              <div className="flex items-start gap-3">
                <Clock size={20} className="text-[#C9A66B] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[#6B6B6B] text-sm">Schedule</p>
                  <p className="text-[#3D3D3D]">Wednesdays</p>
                  <p className="text-[#8B8B8B] text-xs">6:00pm – 7:30pm</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users size={20} className="text-[#C9A66B] mt-1 flex-shrink-0" />
                <div>
                  <p className="text-[#6B6B6B] text-sm">Format</p>
                  <p className="text-[#3D3D3D]">Small group</p>
                  <p className="text-[#8B8B8B] text-xs">Starts April 8</p>
                </div>
              </div>
            </div>
            
            <p className="text-[#6B6B6B] leading-relaxed mb-8">
              Learn how to regulate your nervous system, recognise your patterns, and respond instead of react in real life.
            </p>
            
            <div className="flex items-center justify-between">
              <div>
                <span className="text-[#3D3D3D] text-2xl font-light">$349</span>
              </div>
              <a 
                href="https://buy.stripe.com/3cI14n3OPcV6fKn6eLbAs00"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#C9A66B] text-white px-6 py-3 rounded-full hover:bg-[#B8956A] transition-colors text-sm"
              >
                Join the Program
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-6 bg-[#FAF8F5] border-t border-[#E8E4DE]">
        <div className="max-w-2xl mx-auto text-center">
          <p 
            className="font-serif text-xl text-[#3D3D3D] mb-6"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Somalume — A Gentle Return to the Body
          </p>
          
          <div className="flex items-center justify-center gap-6 text-[#8B8B8B] text-sm">
            <a 
              href="mailto:somalume.au@outlook.com" 
              className="flex items-center gap-2 hover:text-[#C9A66B] transition-colors"
            >
              <Mail size={16} />
              Contact
            </a>
            <a 
              href="https://somalume.org" 
              className="hover:text-[#C9A66B] transition-colors"
            >
              somalume.org
            </a>
          </div>
        </div>
      </footer>
      
    </div>
  );
};

export default Live;
